import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  DND_DRAGGABLE_PROPS,
  Input,
  Text,
  toastManager,
  WidgetCard,
  type WidgetCardProps,
  type WidgetInstance,
} from "@salestrak/ui";
import { useIssueViewMutations } from "#app/hooks/issue-view/use-issue-view-mutations";
import { useCustomFieldValueCommit } from "#app/hooks/issue-view/use-custom-field-value-commit";
import { useIssueViewCapabilities } from "./issue-view-capabilities";
import { AgeLabel } from "#app/components/age/age-label";
import { UpdateAgeDialog } from "#app/components/age/update-age-dialog";
import type {
  IssueViewStage,
  IssueTypeWithFields,
  CustomFieldDef,
} from "./issue-view-types";
import type { EntityRefValue, IssueRow } from "#app/lib/data";
import { IssueTypeSelector } from "#app/components/entity-selectors";
import { EntityTag } from "#app/components/entity";
import { IssueViewProperties } from "./issue-view-properties";
import { IssueViewCustomFields } from "./issue-view-custom-fields";
import { MigrateIssueTypeDialog } from "./migrate-issue-type-dialog";
import { useCreateIssueMode } from "#app/lib/create-issue-context";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import { useWidgetsFlag } from "#app/lib/hooks/use-widgets-flag";
import { toastMutationError } from "#app/lib/utils/toast-errors";
// Direct module path, NEVER the feature barrel: the barrel side-effect-registers
// the issue defs into `#app/widgets/registry`, and the registry's own evaluation
// reaches this file (team-members def → issue-view barrel) — a barrel import
// here would re-enter the half-evaluated registry module and crash.
import {
  IssueWidgetProvider,
  type IssueWidgetNavigateTab,
} from "#app/widgets/issue/issue-widget-context";
import type { ActivityContextValue } from "./activity/activity-context";
import type { NoteNode } from "./activity/activity-types";
import type { IssueMediaItem } from "#app/hooks/issue-view/use-issue-media-items";
import type { Omittable } from "@salestrak/ui";

// Title styling for the name Input: `unstyled` only strips the wrapper's
// chrome, so the height and horizontal padding the control part carries are
// reset through its `data-slot` — part targeting is the sanctioned composition
// seam. Without the px-0 the title would sit 12px right of everything under it.
const TITLE_INPUT =
  "text-2xl font-semibold [&_[data-slot=input]]:h-auto [&_[data-slot=input]]:px-0 [&_[data-slot=input]]:leading-snug";

type IssueViewSummaryProps = Omittable<{
  issue?: IssueRow;
  stage?: IssueViewStage | null;
  workflowId?: string | null;
  address?: {
    id: string;
    addressId: string;
    wholeAddress?: string | null;
    latLng?: { lat: number; lng: number } | null;
  } | null;
  assignees?: EntityRefValue[];
  issueTypes?: IssueTypeWithFields[];
  values?: IssueRow["values"];
  customFields?: CustomFieldDef[];
  onCloseIssue?: () => void;
  propertiesColumns?: 1 | 2;
  contentMaxWidth?: number;
  /** Scroll to + highlight this custom field row on mount. */
  focusCustomFieldId?: string | null;
  // Widget-stack inputs (WIDGETS on): the comments/attachments widgets read
  // the activity feed, notes, and merged media items the issue body already
  // resolves; `onNavigateTab` deep-links out of a widget to another tab.
  onNavigateTab?: (tab: IssueWidgetNavigateTab) => void;
  activity?: ActivityContextValue;
  activityLoading?: boolean;
  notes?: NoteNode[];
  mediaItems?: IssueMediaItem[];
}>;

// ---------------------------------------------------------------------------
// Widget stack (WIDGETS on): properties pinned, then sortable/collapsible
// custom-fields / comments / attachments cards. The stack is client-local —
// hardcoded instances (never persisted to the server), order and collapse in
// localStorage.
// ---------------------------------------------------------------------------

type IssueWidgetStackItem = WidgetInstance & Omittable<{
  collapsible?: boolean;
  hideHeader?: boolean;
}>;

const ISSUE_WIDGET_ORDER_DEFAULTS: string[] = [];

function reconcileIssueWidgetOrder(order: string[], widgets: IssueWidgetStackItem[]) {
  const knownTypes = widgets.map((widget) => widget.type);
  const knownTypeSet = new Set(knownTypes);
  const ordered = order.filter((type) => knownTypeSet.has(type));
  const orderedSet = new Set(ordered);
  return [...ordered, ...knownTypes.filter((type) => !orderedSet.has(type))];
}

function SortableIssueWidgetCard({
  instance,
  collapsed,
  onCollapsedChange,
}: {
  instance: IssueWidgetStackItem;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}) {
  const {
    attributes: { role: _role, ...attributes },
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({ id: instance.type });

  return (
    // Box carries no motion props, so the streamed drag transform/transition
    // are inline style.
    <Box
      direction="column"
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform) ?? undefined,
        transition: isSorting ? transition : undefined,
      }}
    >
      <WidgetCard
        instance={instance}
        canManage={false}
        canArrange
        presentation="stack"
        surface="canvas"
        collapsible={instance.collapsible}
        hideHeader={instance.hideHeader}
        collapsed={collapsed}
        onCollapsedChange={onCollapsedChange}
        isDragging={isDragging}
        dragHandleProps={{
          ...(listeners as object),
          ...(attributes as object),
          ...(DND_DRAGGABLE_PROPS as object),
        } as WidgetCardProps["dragHandleProps"]}
      />
    </Box>
  );
}

const ISSUE_WIDGET_STACK: IssueWidgetStackItem[] = [
  {
    id: "issue-properties",
    type: "issue-properties",
    x: 0,
    y: 0,
    w: 12,
    h: 2,
    config: {},
    schemaVersion: 1,
    hideHeader: true,
  },
  {
    id: "issue-custom-fields",
    type: "issue-custom-fields",
    x: 0,
    y: 2,
    w: 12,
    h: 4,
    config: {},
    schemaVersion: 1,
    collapsible: true,
  },
  {
    id: "issue-comments",
    type: "issue-comments",
    x: 0,
    y: 6,
    w: 12,
    h: 4,
    config: {},
    schemaVersion: 1,
    collapsible: true,
  },
  {
    id: "issue-attachments",
    type: "issue-attachments",
    x: 0,
    y: 10,
    w: 12,
    h: 3,
    config: {},
    schemaVersion: 1,
    collapsible: true,
  },
];

const ISSUE_PROPERTIES_WIDGET = ISSUE_WIDGET_STACK[0]!;
const ISSUE_SORTABLE_WIDGETS = ISSUE_WIDGET_STACK.slice(1);

export function IssueViewSummary({
  issue,
  stage,
  workflowId,
  address,
  assignees,
  issueTypes,
  values,
  customFields,
  onCloseIssue,
  propertiesColumns = 1,
  contentMaxWidth,
  focusCustomFieldId,
  onNavigateTab,
  activity,
  activityLoading,
  notes,
  mediaItems,
}: IssueViewSummaryProps) {
  const createCtx = useCreateIssueMode();
  const isCreateMode = !!createCtx;
  const widgetsOn = useWidgetsFlag();
  const { state: collapsedWidgets, update: updateCollapsedWidgets } = usePersistedState<
    Record<string, boolean>
  >("issue-widgets-collapsed", {});
  // The hook merges objects (read AND update spread into `{}`), so the order
  // array must live under a key — a bare array round-trips as an index-keyed
  // object and crashes on `.filter`.
  const { state: persistedWidgetOrder, update: updatePersistedWidgetOrder } = usePersistedState<{
    order: string[];
  }>("issue-widgets-order", { order: ISSUE_WIDGET_ORDER_DEFAULTS });
  const stackSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  // ---------------------------------------------------------------------------
  // Issue type selection
  // ---------------------------------------------------------------------------

  // The omnibus `updateIssue` drives name renames and type migration;
  // custom-field values commit through the shared `useCustomFieldValueCommit`
  // path; photos-field tiles ride the add/remove delta mutations.
  const {
    updateIssue,
    addPhotoFieldValue,
    removePhotoFieldValue,
  } = useIssueViewMutations();
  const commitCustomFieldValue = useCustomFieldValueCommit();
  // View / comment access → no name edits, no custom field writes,
  // no issue-type migration. Create mode bypasses (no issue yet).
  const { canEdit } = useIssueViewCapabilities();
  const editableHere = isCreateMode || canEdit;

  // ---------------------------------------------------------------------------
  // Issue type migration
  // ---------------------------------------------------------------------------

  const [migrationTarget, setMigrationTarget] = useState<string | null>(null);

  const handleIssueTypeChange = useCallback(
    (issueTypeId: string) => {
      if (isCreateMode) {
        createCtx.updateDraft({ issueTypeId });
        return;
      }
      if (issueTypeId === issue?.issueTypeId) return;
      setMigrationTarget(issueTypeId);
    },
    [isCreateMode, createCtx, issue?.issueTypeId],
  );

  const handleMigrateConfirm = useCallback(
    async (issueTypeId: string, migrateValues: Record<string, unknown>) => {
      if (!issue) return;
      try {
        await updateIssue({
          variables: {
            issueId: issue.issueId,
            input: {
              issueTypeId,
              values:
                Object.keys(migrateValues).length > 0
                  ? migrateValues
                  : undefined,
            },
          },
        });
        toastManager.add({ title: "Project type updated", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to update project type");
      }
      setMigrationTarget(null);
    },
    [issue, updateIssue],
  );

  // ---------------------------------------------------------------------------
  // Age dialog (view mode only)
  // ---------------------------------------------------------------------------

  const [ageDialogOpen, setAgeDialogOpen] = useState(false);
  const handleAgePress = useCallback(() => setAgeDialogOpen(true), []);

  // ---------------------------------------------------------------------------
  // Name update (view mode: mutation on blur; create mode: draft update)
  // ---------------------------------------------------------------------------

  const savedNameRef = useRef(issue?.name ?? "");
  const draftNameRef = useRef(issue?.name ?? "");
  const nameInputRef = useRef<HTMLInputElement>(null);

  // In create mode, focus the title once the container (sheet) signals it's
  // safe to do so. On mobile, focusing during the sheet's open animation
  // opens the virtual keyboard and desyncs the sheet's translateY math. On
  // desktop, `canAutoFocus` defaults to true so this fires on mount.
  const canAutoFocus = createCtx?.canAutoFocus ?? false;
  useEffect(() => {
    if (isCreateMode && canAutoFocus) {
      nameInputRef.current?.focus();
    }
  }, [isCreateMode, canAutoFocus]);

  // Keyed on the ISSUE, not on the issue object: this seeds the name refs when
  // a different issue opens. Depending on `issue` would re-seed them whenever
  // the server echoes a name back, wiping whatever the user is typing.
  useEffect(() => {
    if (!issue) return;
    savedNameRef.current = issue.name;
    draftNameRef.current = issue.name;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue?.issueId]);

  const handleNameChange = useCallback(
    (text: string) => {
      draftNameRef.current = text;
      if (isCreateMode) {
        createCtx.updateDraft({ name: text });
      }
    },
    [isCreateMode, createCtx],
  );

  const handleNameBlur = useCallback(() => {
    if (isCreateMode || !issue) return;
    if (!editableHere) return;
    const trimmed = draftNameRef.current.trim();
    if (trimmed && trimmed !== savedNameRef.current) {
      savedNameRef.current = trimmed;
      // Use the omnibus UpdateIssue mutation — server returns the
      // full IssueViewIssue fragment, Apollo merges by id. Skip the
      // optimistic response since a blur-driven rename doesn't need
      // sub-roundtrip latency masking, and crafting a full
      // IssueViewIssue payload here would be brittle.
      void updateIssue({
        variables: { issueId: issue.issueId, input: { name: trimmed } },
      });
    }
  }, [isCreateMode, editableHere, updateIssue, issue]);

  // ---------------------------------------------------------------------------
  // Custom field value updates
  // ---------------------------------------------------------------------------

  // Create mode writes into the draft; view mode commits through the shared
  // single-field write path (`undefined` is the row's clear signal — a clear
  // deletes the row rather than storing an empty value).
  const handleCustomFieldUpdate = useCallback(
    (data: { customFieldId: string; value: unknown }) => {
      if (isCreateMode) {
        createCtx.updateCustomFieldValue(data.customFieldId, data.value);
        return;
      }
      if (!issue || !editableHere) return;
      commitCustomFieldValue(
        { issueId: issue.issueId, values: values ?? null },
        data.customFieldId,
        data.value,
      );
    },
    [isCreateMode, createCtx, issue, editableHere, values, commitCustomFieldValue],
  );

  // Photos-field delta writes (view mode only): each landed upload / removed
  // tile commits a single-photo add/remove, so concurrent uploads can't
  // clobber each other.
  const handleAddPhoto = useCallback(
    (customFieldId: string, photoId: string) => {
      if (isCreateMode || !issue || !editableHere) return;
      return addPhotoFieldValue(issue.issueId, customFieldId, photoId);
    },
    [isCreateMode, issue, editableHere, addPhotoFieldValue],
  );
  const handleRemovePhoto = useCallback(
    (customFieldId: string, photoId: string) => {
      if (isCreateMode || !issue || !editableHere) return;
      return removePhotoFieldValue(issue.issueId, customFieldId, photoId);
    },
    [isCreateMode, issue, editableHere, removePhotoFieldValue],
  );

  // ---------------------------------------------------------------------------
  // Resolve data sources for create vs view mode
  // ---------------------------------------------------------------------------

  const resolvedIssueTypes = isCreateMode ? createCtx.issueTypes : issueTypes;
  const resolvedIssueTypeId = isCreateMode ? createCtx.draft.issueTypeId : issue?.issueTypeId;
  const resolvedValues = isCreateMode ? createCtx.draft.values : (values ?? undefined);
  const resolvedCustomFields = isCreateMode ? createCtx.customFieldDefs : customFields;
  const resolvedRequiredFieldIds = isCreateMode ? createCtx.requiredFieldIds : undefined;

  // Server returns IssueType.fields in sortOrder ASC. This list drives the
  // custom-fields table row order so the order users set on the project
  // type edit screen is honored everywhere.
  const orderedTypeFieldIds = useMemo<string[]>(() => {
    if (isCreateMode) {
      return createCtx.issueTypeFields
        .filter((f) => f.field !== null)
        .map((f) => f.customFieldId);
    }
    if (!resolvedIssueTypeId || !issueTypes) return [];
    const type = issueTypes.find(
      (it) => it.issueTypeId === resolvedIssueTypeId,
    );
    return (type?.fields ?? [])
      .filter((f) => f.field !== null)
      .map((f) => f.customFieldId);
  }, [isCreateMode, createCtx, issueTypes, resolvedIssueTypeId]);

  // ---------------------------------------------------------------------------
  // Widget stack order (WIDGETS on)
  // ---------------------------------------------------------------------------

  const orderedSortableWidgets = useMemo(() => {
    const order = reconcileIssueWidgetOrder(persistedWidgetOrder.order, ISSUE_SORTABLE_WIDGETS);
    return order
      .map((type) => ISSUE_SORTABLE_WIDGETS.find((widget) => widget.type === type))
      .filter((widget): widget is IssueWidgetStackItem => Boolean(widget));
  }, [persistedWidgetOrder]);
  // Create mode shows just properties + custom fields (no feed to comment on,
  // no attachments yet), fixed order.
  const createModeWidgets = ISSUE_WIDGET_STACK.slice(0, 2);

  const handleWidgetDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const currentOrder = orderedSortableWidgets.map((widget) => widget.type);
      const oldIndex = currentOrder.findIndex((type) => type === active.id);
      const newIndex = currentOrder.findIndex((type) => type === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      updatePersistedWidgetOrder({ order: arrayMove(currentOrder, oldIndex, newIndex) });
    },
    [orderedSortableWidgets, updatePersistedWidgetOrder],
  );

  // The stack cards' disclosure gutter shifts card titles right; the page
  // header sections take the same left inset so everything lines up.
  const stackGutter = widgetsOn ? ("lg" as const) : undefined;

  return (
    <>
      <Box padding="md" gap="sm" align="center">
        <Box fill="width" maxWidth={contentMaxWidth} gap="sm">
          {/* Issue type selector + age. Both are interactive in
              create / edit mode but render as plain badges in view /
              comment so the user can't change type or open the age
              dialog. */}
          <Box direction="row" align="center" gap="sm" paddingLeft={stackGutter}>
            <Box maxWidth={350}>
              {editableHere ? (
                <IssueTypeSelector
                  issueTypes={
                    resolvedIssueTypes && resolvedIssueTypes.length > 0
                      ? resolvedIssueTypes
                      : issue?.issueType
                        ? [issue.issueType]
                        : []
                  }
                  value={resolvedIssueTypeId}
                  onValueChange={handleIssueTypeChange}
                  size="sm"
                  valueMuted
                />
              ) : issue?.issueType ? (
                <EntityTag
                  entity={{
                    kind: "issueType",
                    id: issue.issueType.issueTypeId,
                    issueType: issue.issueType,
                  }}
                  size="sm"
                  mutedLabel
                  interactive={false}
                />
              ) : null}
            </Box>

            {!isCreateMode && issue && (
              <AgeLabel
                age={issue.age}
                size="sm"
                buttonSize="sm"
                buttonVariant="outline"
                onClick={editableHere ? handleAgePress : undefined}
              />
            )}
          </Box>

          {/* Title — Input when editable, static Text when read-only.
              Tier:
                view / comment → no input, just the name text.
                edit / full_edit → editable Input, renames on blur. */}
          <Box
            paddingY="md"
            id={isCreateMode ? "create-issue-name" : undefined}
            paddingLeft={stackGutter}
          >
            {editableHere ? (
              <Input
                ref={nameInputRef}
                key={isCreateMode ? "create" : issue?.issueId}
                defaultValue={
                  isCreateMode ? (createCtx?.draft.name ?? "") : (issue?.name ?? "")
                }
                onValueChange={handleNameChange}
                onBlur={handleNameBlur}
                placeholder="Project title..."
                unstyled
                className={TITLE_INPUT}
              />
            ) : (
              <Text size="xl" weight="bold" truncate={2}>
                {issue?.name ?? ""}
              </Text>
            )}
          </Box>

          {widgetsOn ? (
            <IssueWidgetProvider
              value={{
                issue,
                stage,
                workflowId,
                address,
                assignees,
                issueTypes: resolvedIssueTypes,
                values: resolvedValues,
                customFields: resolvedCustomFields,
                orderedTypeFieldIds,
                requiredFieldIds: resolvedRequiredFieldIds,
                propertiesColumns,
                onUpdateValue: handleCustomFieldUpdate,
                onAddPhoto: !isCreateMode && editableHere ? handleAddPhoto : undefined,
                onRemovePhoto: !isCreateMode && editableHere ? handleRemovePhoto : undefined,
                onCloseIssue,
                focusCustomFieldId,
                isCreateMode,
                onNavigateTab,
                activity,
                activityLoading,
                notes,
                mediaItems,
              }}
            >
              {isCreateMode ? (
                <Box gap="md" paddingLeft={stackGutter}>
                  {createModeWidgets.map((instance, index) => (
                    <WidgetCard
                      key={`${instance.id}-${index}`}
                      instance={instance}
                      canManage={false}
                      presentation="stack"
                      surface="canvas"
                      collapsible={instance.collapsible}
                      hideHeader={instance.hideHeader}
                      collapsed={collapsedWidgets[instance.type] ?? false}
                      onCollapsedChange={(collapsed) =>
                        updateCollapsedWidgets({ [instance.type]: collapsed })
                      }
                    />
                  ))}
                </Box>
              ) : (
                <Box gap="md" paddingLeft={stackGutter}>
                  <WidgetCard
                    key={`${ISSUE_PROPERTIES_WIDGET.id}-0`}
                    instance={ISSUE_PROPERTIES_WIDGET}
                    canManage={false}
                    presentation="stack"
                    surface="canvas"
                    hideHeader={ISSUE_PROPERTIES_WIDGET.hideHeader}
                  />
                  <DndContext
                    sensors={stackSensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleWidgetDragEnd}
                  >
                    <SortableContext
                      items={orderedSortableWidgets.map((widget) => widget.type)}
                      strategy={verticalListSortingStrategy}
                    >
                      {orderedSortableWidgets.map((instance, index) => (
                        <SortableIssueWidgetCard
                          key={`${instance.id}-${index + 1}`}
                          instance={instance}
                          collapsed={collapsedWidgets[instance.type] ?? false}
                          onCollapsedChange={(collapsed) =>
                            updateCollapsedWidgets({ [instance.type]: collapsed })
                          }
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </Box>
              )}
            </IssueWidgetProvider>
          ) : (
            <>
              {/* Properties grid */}
              <Box
                padding="md"
                rounded="sm"
                gap="sm"
                border
              >
                <Text size="lg" weight="semibold">
                  Properties
                </Text>
                <IssueViewProperties
                  issue={issue}
                  stage={stage}
                  workflowId={workflowId}
                  address={address}
                  assignees={assignees}
                  columns={propertiesColumns}
                />
              </Box>

              {/* Custom fields */}
              <IssueViewCustomFields
                issue={issue}
                values={resolvedValues}
                customFields={resolvedCustomFields}
                orderedTypeFieldIds={orderedTypeFieldIds}
                onUpdateValue={handleCustomFieldUpdate}
                onAddPhoto={!isCreateMode && editableHere ? handleAddPhoto : undefined}
                onRemovePhoto={!isCreateMode && editableHere ? handleRemovePhoto : undefined}
                requiredFieldIds={resolvedRequiredFieldIds}
                onCloseIssue={onCloseIssue}
                focusCustomFieldId={focusCustomFieldId}
              />
            </>
          )}
        </Box>
      </Box>

      {!isCreateMode && issue && (
        <UpdateAgeDialog
          open={ageDialogOpen}
          onOpenChange={setAgeDialogOpen}
          issueId={issue.issueId}
          stageId={stage?.stageId ?? issue.stageId}
          age={issue.age}
        />
      )}

      {!isCreateMode && issue?.issueTypeId && resolvedIssueTypes && migrationTarget && (
        <MigrateIssueTypeDialog
          open
          onOpenChange={(o) => {
            if (!o) setMigrationTarget(null);
          }}
          issueTypes={resolvedIssueTypes}
          issueTypeId={migrationTarget}
          values={resolvedValues ?? {}}
          issueName={issue.name}
          onConfirm={handleMigrateConfirm}
        />
      )}
    </>
  );
}
